import { FetchStatus, fetchUpdate } from '@insights-gaming/redux-utils';
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { makeStatisticsActions } from '../actions';
import { makeStatisticsSagas } from '../sagas';
import actionCreatorFactory from 'typescript-fsa';
import { Riot } from '../types/league-of-legends';
import { LeagueGameEvent } from '@insights-gaming/game-events';

type Dictionary<T> = Record<string, T>;
export interface LeagueStats {
  leagueAssets: Partial<Dictionary<Riot.League.Assets>>;
  assetsFetchStatus: Partial<Dictionary<FetchStatus>>;
  leagueStats: Partial<Dictionary<Riot.League.RawDataInfo>>;
  fetchStatus: Partial<Dictionary<FetchStatus>>;
  currentLeagueGameVersion?: string;
}
export interface StatisticsState {
  [LeagueGameEvent.CLASS_ID]: LeagueStats;
}

interface StatisticsSliceConfig {
  extraReducers?: (builder: ActionReducerMapBuilder<StatisticsState>) => void;
}

export function makeStatisticsSlice(config: StatisticsSliceConfig = {}) {
  const { extraReducers } = config;
  const name = '@insights-gaming/statistics-slice';
  const actionCreator = actionCreatorFactory(name);

  const statisticsActions = makeStatisticsActions(actionCreator);

  const {
    fetchLeagueOfLegendsAssetsAC,
    fetchLeagueOfLegendsCurrentVersionAC,
    fetchLeagueOfLegendsStatsAC,
  } = statisticsActions;

  const initialState: StatisticsState = {
    [LeagueGameEvent.CLASS_ID]: {
      leagueAssets: {},
      assetsFetchStatus: {},
      leagueStats: {},
      fetchStatus: {},
    },
  };

  const statisticsSlice = createSlice({
    name,
    initialState,
    reducers: {},
    extraReducers: builder => {
      builder.addCase(fetchLeagueOfLegendsCurrentVersionAC.done, leagueReducer((state, action) => {
        state.currentLeagueGameVersion = action.payload.result;
      }));
      {
        const async = fetchLeagueOfLegendsAssetsAC;
        builder.addCase(async.started, leagueReducer((state, action) => {
          const { gameVersion } = action.payload;

          state.assetsFetchStatus[gameVersion] = fetchUpdate(
            state.assetsFetchStatus[gameVersion] || ({done: false, fetching: false}),
            { $fetchStarted: true },
          );
        }));
        builder.addCase(async.done, leagueReducer((state, action) => {
          const { result, params } = action.payload;
          const { gameVersion } = params;

          state.assetsFetchStatus[gameVersion] = fetchUpdate(
            state.assetsFetchStatus[gameVersion] || ({done: false, fetching: false}),
            { $fetchDone: true },
          );
          state.leagueAssets[gameVersion] = result;
        }));
        builder.addCase(async.failed, leagueReducer((state, action) => {
          const { error, params } = action.payload;
          const { gameVersion } = params;

          state.assetsFetchStatus[gameVersion] = fetchUpdate(
            state.assetsFetchStatus[gameVersion] || ({done: false, fetching: false}),
            { $fetchFailed: [error] },
          );
        }));
      }

      {
        const async = fetchLeagueOfLegendsStatsAC;
        builder.addCase(async.started, leagueReducer((state, action) => {
          const { videoUuid } = action.payload;

          state.fetchStatus[videoUuid] = fetchUpdate(
            state.fetchStatus[videoUuid] || ({done: false, fetching: false}),
            { $fetchStarted: true },
          );
        }));
        builder.addCase(async.done, leagueReducer((state, action) => {
          const { result, params } = action.payload;
          const { videoUuid } = params;

          state.fetchStatus[videoUuid] = fetchUpdate(
            state.fetchStatus[videoUuid] || ({done: false, fetching: false}),
            { $fetchDone: true },
          );
          state.leagueStats[videoUuid] = result;
        }));
        builder.addCase(async.failed, leagueReducer((state, action) => {
          const { error, params } = action.payload;
          const { videoUuid } = params;

          state.fetchStatus[videoUuid] = fetchUpdate(
            state.fetchStatus[videoUuid] || ({done: false, fetching: false}),
            { $fetchFailed: [error] },
          );
        }));
      }

      if (extraReducers) {
        extraReducers(builder);
      }
    },
  });

  const { allStatisticsSagas, statisticsSagas } = makeStatisticsSagas(statisticsActions);

  return {
    allStatisticsSagas,
    statisticsActions,
    statisticsSagas,
    statisticsSlice,
  };
}

function leagueReducer<A>(reducer: (league: StatisticsState[typeof LeagueGameEvent.CLASS_ID], action: A) => StatisticsState | void) {
  return function (state: StatisticsState, action: A) {
    return reducer(state[LeagueGameEvent.CLASS_ID], action);
  };
}
